import React, { useEffect, useState } from "react";
import { FaEye, FaEyeSlash, FaTimes } from "react-icons/fa";
import api from "../../services/apiService";
import { validatePassword, PASSWORD_REQUIREMENTS_MESSAGE } from "../../utils/passwordValidation";

const getUserDisplayName = (user) =>
  user?.Name || user?.name || [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.Email || user?.email || "this user";

const ResetPasswordModal = ({ show, handleClose, user, onSuccess }) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setNewPassword("");
      setConfirmPassword("");
      setShowPassword(false);
      setError("");
    }
  }, [show]);

  if (!show) return null;

  const targetId = user?.id || user?.userId;

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    const { valid, message } = validatePassword(newPassword);
    if (!valid) {
      setError(message);
      return;
    }

    if (!targetId) {
      setError("Unable to reset password: missing user id.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      await api.put(`/update-user/${targetId}/password`, { newPassword, confirmPassword });
      if (onSuccess) onSuccess();
      handleClose();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to update password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 dark:bg-black/60 p-3 sm:p-6">
      <button
        type="button"
        aria-label="Close reset password modal"
        onClick={handleClose}
        className="absolute inset-0"
      />

      <div className="relative w-full max-w-md rounded-2xl border border-app-primary bg-app-card shadow-2xl dark:shadow-black/50">
        <div className="flex items-start justify-between border-b border-app-primary px-5 py-4">
          <div>
            <h3 className="text-xl font-semibold text-app-heading">Reset Password</h3>
            <p className="text-sm text-app-muted">
              Set a new password for {getUserDisplayName(user)}.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg p-2 text-app-muted transition-colors hover:bg-app-hover hover:text-app-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-5">
          <div className="space-y-4">
            <div>
              <label htmlFor="reset-new-password" className="mb-1 block text-sm font-medium text-app-secondary">
                New Password
              </label>
              <div className="relative">
                <input
                  id="reset-new-password"
                  type={showPassword ? "text" : "password"}
                  className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 pr-11 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-app-muted transition-colors hover:text-app-primary"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              <p className="mt-1 text-xs text-app-muted">{PASSWORD_REQUIREMENTS_MESSAGE}</p>
            </div>

            <div>
              <label htmlFor="reset-confirm-password" className="mb-1 block text-sm font-medium text-app-secondary">
                Confirm Password
              </label>
              <input
                id="reset-confirm-password"
                type={showPassword ? "text" : "password"}
                className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                required
              />
            </div>

            {error && <p className="text-sm font-medium text-app-error">{error}</p>}
          </div>

          <div className="mt-6 flex justify-end gap-3 border-t border-app-primary pt-4">
            <button type="button" onClick={handleClose} disabled={loading} className="rounded-lg border border-app-primary px-5 py-2 text-sm font-semibold text-app-secondary transition hover:bg-app-hover hover:text-app-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-60">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="rounded-lg border border-[#2470B7] bg-[#2470B7] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#1f5f9b] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-60 dark:border-blue-600 dark:bg-blue-600 dark:hover:bg-blue-500">
              {loading ? "Saving..." : "Save Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordModal;
